import * as React from 'react';

import { useCan } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { PlusIcon } from '@/icons/plus-icon';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { CommissionSlabs } from '@/services/slabs/slabs.types';
import { useParams, useSearch } from '@tanstack/react-router';
import { useCommissionStatusToggle } from '../../hooks/useCommissionStatusToggle';
import { useSlabDelete } from '../../hooks/useSlabDelete';
import { ManageCommissionSlabContainer } from '../../ManageCommissionSlabsContainer';
import { getColumns } from './Column';
import { LockCloseIcon } from '@/icons/lock-close.icon';

export function SlabsTable({
  data,
  isLoading = false,
  pagination,
  categoryName,
}: {
  data: CommissionSlabs[];
  isLoading: boolean;
  pagination: Pagination;
  categoryName?: string;
}) {
  const search = useSearch({ from: '/(app)/configuration-management/commissions/$categoryId/' });
  const { adminCommissionTypeId } = search;
  const { categoryId } = useParams({
    from: '/(app)/configuration-management/commissions/$categoryId',
  });
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const canAddNew = useCan({ allOf: [PERMS.commission.detail.manage] });
  const canBlock = useCan({ allOf: [PERMS.commission.category.statusUpdate] });
  const { openConfirmation } = useConfirmationStore();

  const { mutateAsync } = useSlabDelete();
  const { mutateAsync: statusUpdateMutate } = useCommissionStatusToggle();

  const columns = React.useMemo(
    () => getColumns(onOpen, mutateAsync, openConfirmation, t, adminCommissionTypeId, categoryId),
    [onOpen, mutateAsync, openConfirmation, t, adminCommissionTypeId, categoryId],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Add New Commission Slab',
      okText: <CrumbTitle id="common.page.button.submit" />,
      size: 'lg',
      modalTitle: 'configurationManagement.commissions.manageSlabsModal.title',
      component: (
        <ManageCommissionSlabContainer
          categoryId={categoryId}
          adminCommissionTypeId={adminCommissionTypeId}
          categoryName={categoryName}
        />
      ),
    });
  };
  const onBlock = () => {
    openConfirmation({
      title: t('common.page.modal.confirm'),
      description: t('configurationManagement.commissions.blockSlabModal.blockCategorySlab'),
      cancelLabel: t('common.page.modal.no'),
      actionLabel: t('common.page.modal.submit'),
      size: 'lg',
      onAction: async () => {
        await statusUpdateMutate({
          categoryId,
          status: 'B',
        });
      },
      variant: 'warning',
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'sn', desc: true }],
    },
    meta: {
      title: categoryName,
      actions: [
        {
          key: 'onBlock',
          icon: <LockCloseIcon />,
          onClick: canBlock ? onBlock : () => {},
        },
        {
          key: 'onAddNew',
          icon: <PlusIcon />,
          onClick: canAddNew ? onAddNew : () => {},
        },
      ],
      loading: isLoading,
      totalPage: pagination.totalItems,
      hideSearch: true,
    },
    getRowId: (originalRow) => String(originalRow.sn),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="rounded-t-none">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
